import {expandBox, intersectBoxes, resolveWindVector} from './biomeMath';

/**
 * Что эффект подложки знает о поле: где лежит картинка пейзажа, где игровые
 * ячейки, куда дует ветер матча. Собирается один раз на поле (см.
 * scenes/FieldBiomeEffects) и передается построителям эффектов.
 *
 * Точки картинки пейзажа эффекты задают в долях ее стороны (u, v: 0..1 от
 * левого верхнего угла) — картинка масштабируется вместе с полем, и нарисованные
 * на ней озера, гейзеры и лабиринты остаются на своих местах. Размеры того,
 * что живет над полем, — в размерах ячейки.
 *
 * @param {Object} params
 * @param {{left: number, right: number, top: number, bottom: number}} params.rect —
 *   прямоугольник картинки пейзажа
 * @param {number} params.hexSize
 * @param {Array<{x: number, y: number}>} [params.cellCenters] — центры игровых ячеек
 * @param {{sample: Function}} [params.coverage] — карта покрытия поля (см.
 *   buildFieldCoverage): 0 — далеко от поля, 0.5 — кромка, 1 — глубина
 * @param {{direction: number, tiltRad: number}} [params.drift] — ветер матча
 * @param {string} [params.groundBackgroundId]
 * @param {Array<{x: number, y: number}>} [params.mountains] — центры ячеек с
 *   горами: у гор эффекты завихряются (вьюга)
 * @returns {Object|null} null — картинки пейзажа или размера ячейки нет
 */
export const createBiomeContext = ({
  rect, hexSize, cellCenters, coverage, drift, groundBackgroundId, mountains,
} = {}) => {
  const isRectValid = rect && rect.right > rect.left && rect.bottom > rect.top;
  if (!isRectValid || !(hexSize > 0)) {
    return null;
  }

  const cells = cellCenters || [];
  const side = rect.right - rect.left;
  const height = rect.bottom - rect.top;
  // Рамка игровых ячеек; поля нет (редактор до первой ячейки) — середина пейзажа
  let fieldBox = {
    left: rect.left + side * 0.25, right: rect.right - side * 0.25,
    top: rect.top + height * 0.25, bottom: rect.bottom - height * 0.25,
  };
  if (cells.length > 0) {
    fieldBox = {left: Infinity, right: -Infinity, top: Infinity, bottom: -Infinity};
    for (let index = 0; index < cells.length; index += 1) {
      fieldBox.left = Math.min(fieldBox.left, cells[index].x - hexSize);
      fieldBox.right = Math.max(fieldBox.right, cells[index].x + hexSize);
      fieldBox.top = Math.min(fieldBox.top, cells[index].y - hexSize);
      fieldBox.bottom = Math.max(fieldBox.bottom, cells[index].y + hexSize);
    }
  }

  const context = {
    rect,
    side,
    hexSize,
    cells,
    fieldBox,
    coverage: coverage || {sample: () => 1},
    drift: drift || {direction: 1, tiltRad: 0},
    wind: resolveWindVector(drift),
    groundBackgroundId,
    // Горы поля. Список один на все время жизни контекста: горы ставят и
    // убирают (редактор поля) — меняется его содержимое, и построители,
    // читающие список при рождении группы, видят нынешние горы
    mountains: [...(mountains || [])],
    /** Точка картинки пейзажа (доли стороны) -> точка мира */
    toWorld: (u, v) => ({x: rect.left + u * side, y: rect.top + v * height}),
    /** Овал на картинке пейзажа (доли стороны) -> овал в мире */
    toWorldOval: ({u, v, radiusU, radiusV}) => ({
      x: rect.left + u * side,
      y: rect.top + v * height,
      radiusX: radiusU * side,
      radiusY: (radiusV ?? radiusU) * height,
    }),
  };
  return context;
};

/**
 * Где рождать частицы: то, что сейчас видит камера (с запасом), в пределах
 * области эффекта. Частиц немного, и рассыпать их по всему пейзажу значило бы
 * оставить в кадре считаные единицы.
 *
 * @param {Object} context — из createBiomeContext
 * @param {{left: number, right: number, top: number, bottom: number}|null} view —
 *   видимая камерой часть мира; null — камеры нет
 * @param {Object} [options]
 * @param {number} [options.fieldMarginFactor] — запас вокруг рамки поля, размеров ячейки
 * @param {number} [options.viewMarginFactor] — запас вокруг кадра, размеров ячейки
 * @returns {{left: number, right: number, top: number, bottom: number}}
 */
export const resolveSpawnBox = (context, view, {fieldMarginFactor = 2, viewMarginFactor = 2} = {}) => {
  const fieldArea = intersectBoxes(expandBox(context.fieldBox, context.hexSize * fieldMarginFactor), context.rect)
    || context.rect;
  if (!view) {
    return fieldArea;
  }
  // Камеру можно увести далеко за поле: там эффекту рождаться негде — остается поле
  return intersectBoxes(expandBox(view, context.hexSize * viewMarginFactor), fieldArea) || fieldArea;
};
